import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HowItWorks from '../components/how-it-works';
import WhyChooseUsBento from '../components/ui/why-choose-us-bento';
import userService from '../services/userService';
import SEO from '../components/SEO';
import forInfluencersHero from '../assets/for-influencers-hero.png';
import {
  DollarSign,
  Users,
  Star,
  ShoppingBag,
  BarChart3,
  Lightbulb,
  Camera,
  Hash,
  Heart,
  Phone,
  MessageCircle,
  Rocket
} from 'lucide-react';

const ForInfluencers = () => {
  // Controls for animations
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    });
  }, [controls]);

  // Benefits data
  const benefits = [
    {
      icon: DollarSign,
      title: "Monetize Your Influence",
      description: "Earn money and receive products by creating authentic content for brands you love."
    },
    {
      icon: ShoppingBag,
      title: "Access Premium Brands",
      description: "Work with established brands across various industries and build your portfolio."
    },
    {
      icon: Users,
      title: "Grow Your Audience",
      description: "Expand your reach by collaborating with brands that align with your personal brand."
    },
    {
      icon: BarChart3,
      title: "Track Your Performance",
      description: "Access analytics to understand your impact and improve your content strategy."
    },
    {
      icon: Star,
      title: "Build Your Reputation",
      description: "Establish yourself as a trusted voice in your niche through verified reviews."
    },
    {
      icon: Lightbulb,
      title: "Creative Freedom",
      description: "Express your authentic voice while creating content that resonates with your audience."
    }
  ];

  // Success stories — real influencer profile pictures from Firestore
  const [successStories, setSuccessStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);

  useEffect(() => {
    const fetchSuccessStories = async () => {
      try {
        setLoadingStories(true);
        const influencers = await userService.getAllInfluencers();

        // Only keep influencers that have a profile picture
        const withPhotos = influencers
          .filter((u) => u.profileImageUrl || u.photoURL)
          .map((u) => ({
            id: u.id,
            name: u.displayName || 'Influencer',
            image: u.profileImageUrl || u.photoURL,
          }))
          .slice(0, 3);

        setSuccessStories(withPhotos);
      } catch (error) {
        console.error('Error fetching influencer profiles:', error);
        setSuccessStories([]);
      } finally {
        setLoadingStories(false);
      }
    };

    fetchSuccessStories();
  }, []);

  return (
    <div className="bg-white min-h-screen relative overflow-hidden">
      <SEO
        title="For Influencers — Monetize Your Content with Brand Campaigns"
        description="Join Engaged Manila as an influencer. Browse campaigns, collaborate with brands, create authentic product reviews, and get paid for your content."
        url="/for-influencers"
      />
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-15"></div>
        
        {/* Social media-related floating icons */}
        {[Camera, Hash, Heart, Star, Phone, MessageCircle].map((Icon, i) => (
          <motion.div
            key={`social-icon-${i}`}
            className="absolute text-primary opacity-20"
            style={{
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 90 + 5}%`,
              fontSize: `${Math.random() * 1 + 0.8}rem`
            }}
            animate={{
              y: [0, Math.random() * 30 - 15],
              rotate: [0, Math.random() * 20 - 10]
            }}
            transition={{
              duration: Math.random() * 8 + 7,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Icon className="w-12 h-12" />
          </motion.div>
        ))}
        
        {/* Decorative circles - representing social media bubbles */}
        <motion.div 
          className="absolute rounded-full bg-primary/10 w-96 h-96 -right-20 top-1/4"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-blue-400/10 w-64 h-64 -left-10 top-2/3"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Social media engagement dots - like likes and interactions */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={`engagement-dot-${i}`}
            className="absolute rounded-full bg-primary"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.2
            }}
            animate={{
              x: [0, Math.random() * 40 - 20],
              y: [0, Math.random() * 40 - 20],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
        
        {/* Follower connection lines */}
        {[...Array(10)].map((_, i) => (
          <motion.div 
            key={`follower-line-${i}`}
            className="absolute bg-primary h-px opacity-20"
            style={{
              width: `${Math.random() * 30 + 10}%`,
              left: `${Math.random() * 90}%`,
              top: `${Math.random() * 100}%`,
              transformOrigin: 'left',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.2
            }}
          />
        ))}
      </div>
      <Header />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-blue-700 pt-24 pb-16 relative overflow-hidden">
        {/* Hero section background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Social media followers visualization */}
          {[...Array(15)].map((_, i) => (
            <motion.div 
              key={`follower-node-${i}`}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 6 + 4}px`,
                height: `${Math.random() * 6 + 4}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: 0.3
              }}
              animate={{
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
          ))}
          
          {/* Content creation waves */}
          {[...Array(5)].map((_, i) => (
            <motion.div 
              key={`content-wave-${i}`}
              className="absolute bg-white h-1 rounded-full"
              style={{
                width: `${Math.random() * 20 + 10}%`,
                left: `${Math.random() * 80}%`,
                top: `${20 + i * 15}%`,
                transformOrigin: 'left',
                opacity: 0.15
              }}
              animate={{
                scaleX: [1, 1.2, 1],
                opacity: [0.15, 0.25, 0.15]
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.5
              }}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <motion.div 
              className="md:w-1/2 md:pr-8"
              initial={{ opacity: 0, y: 20 }}
              animate={controls}
            >
              <h1 className="text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
                Turn Your Influence Into Income
              </h1>
              <p className="mt-3 max-w-md text-base text-blue-100 sm:text-lg md:mt-5 md:text-xl">
                Join our community of creators and get paid to share authentic reviews and content about products you love
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/register?type=influencer"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-primary bg-white hover:bg-gray-100 transition-colors duration-300"
                  >
                    Join as Influencer
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/campaigns"
                    className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-600 transition-colors duration-300"
                  >
                    Browse Campaigns
                  </Link>
                </motion.div>
              </div>
            </motion.div>
            <motion.div 
              className="mt-10 md:mt-0 md:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              <img
                src={forInfluencersHero}
                alt="Influencer creating content"
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Benefits Section */}
      <WhyChooseUsBento
        eyebrow="Benefits"
        title="Why Influencers Love Us"
        subtitle="Discover how our platform helps you grow your influence and income"
        features={benefits.map((b) => ({ title: b.title, description: b.description, icon: b.icon }))}
        stats={[
          { value: "500+", emoji: "💰", label: "Paid Reviews" },
          { value: "100+", emoji: "🏷️", label: "Brand Partners" },
          { value: "24/7", emoji: "🛠️", label: "Creator Support" },
        ]}
        illustration={{
          question: "Which campaigns match my niche and audience best?",
          searchLabel: "Browse campaigns",
        }}
      />
      
      {/* How It Works Section */}
      <div id="how-it-works" className="bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Process</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              How It Works
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Our simple 5-step process to start earning as an influencer
            </p>
          </motion.div>
        </div>

        <HowItWorks
          features={[
            {
              title: "Create Your Profile",
              description: "Sign up and showcase your social media presence, niche, and content style.",
              colorTheme: "orange",
            },
            {
              title: "Browse Campaigns",
              description: "Explore available campaigns that match your interests and audience.",
              colorTheme: "blue",
            },
            {
              title: "Apply to Campaigns",
              description: "Submit applications to campaigns you're interested in with your content ideas.",
              colorTheme: "purple",
            },
            {
              title: "Create Authentic Content",
              description: "Once approved, receive products and create honest, engaging content.",
              colorTheme: "orange",
            },
            {
              title: "Get Paid",
              description: "Publish your content, receive payment, and build your influencer portfolio.",
              colorTheme: "blue",
            },
          ]}
        />
      </div>
      
      {/* Our Influencers Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Our Community</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Meet Our Influencers
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Real creators from our platform
            </p>
          </motion.div>
          
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {loadingStories ? (
              <div className="col-span-full flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                <p className="ml-4 text-gray-500">Loading...</p>
              </div>
            ) : successStories.length > 0 ? (
              successStories.map((story, index) => (
                <motion.div 
                  key={story.id || index}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="relative h-60">
                    <img 
                      src={story.image} 
                      alt={story.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-lg font-bold text-gray-900">{story.name}</h3>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Influencers Yet</h3>
                <p className="text-gray-500">Influencer profiles will appear here once available.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Pre-launch Message Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Join Now</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Start Your Influencer Journey
            </p>
          </motion.div>
          
          <motion.div 
            className="mt-10 bg-white shadow-lg rounded-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="p-10 text-center">
              <div className="flex justify-center mb-6">
                <Rocket className="h-16 w-16 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Platform Now Live</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
                Engaged Manila is now live! Join our community of influencers and access exclusive campaigns from top brands across the Philippines and Southeast Asia.
              </p>
              <div className="mt-8 flex justify-center">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/register"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark transition-colors duration-300"
                  >
                    Register Now
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-primary">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              <span className="block">Ready to monetize your influence?</span>
              <span className="block text-blue-100">Join our community today.</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-blue-100">
              Create your free profile and start applying to paid campaigns in minutes.
            </p>
          </motion.div>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <motion.div 
              className="inline-flex rounded-md shadow"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/register?type=influencer"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50"
              >
                Sign up now
              </Link>
            </motion.div>
            <motion.div 
              className="ml-3 inline-flex rounded-md shadow"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/campaigns"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50"
              >
                Browse campaigns
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </p>
          </motion.div>
          
          <div className="mt-12 max-w-3xl mx-auto">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How much can I earn as an influencer?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      Earnings vary based on your follower count, engagement rate, and niche. Campaigns typically range from ₱2,000 to ₱50,000+ depending on these factors and the campaign requirements.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How many followers do I need to join?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      We welcome influencers of all sizes, from nano (1K-10K) to macro (100K+). What matters most is your engagement rate and the authenticity of your content.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How do I get paid?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      Payments are processed through our secure platform via bank transfer, GCash, or PayPal, typically within 7-14 days after campaign completion and content approval.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Do I have to accept every campaign?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      No, you have complete freedom to apply only to campaigns that align with your personal brand and interests. We encourage authentic partnerships.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForInfluencers;
